import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface part {
  titre: string;
  numberOfQuestions: number;
  numberOfAnswers: number;
}

interface Question {
  questionNumber: number;
  numberOfAnswers: number;
  partIndex: number;
}

interface AnswerChoice {
  letter: string;
  text: string;
  isCorrect: boolean;
}

interface DetailedQuestion extends Question {
  id?: number;
  isNew?: boolean;
  questionText: string;
  points: number;
  answerChoices: AnswerChoice[];
}

interface LocationState {
  testTitle: string;
  testType: string;
  testId?: number;
  mode?: 'create' | 'edit';
  questions: Question[];
  parts: part[];
}

const allLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

const isQuestionValid = (q: DetailedQuestion): boolean => {
  if (!q.questionText.trim()) return false;
  for (const c of q.answerChoices) {
    if (!c.text.trim()) return false;
  }
  return q.answerChoices.some(c => c.isCorrect);
};

const DetailedTestConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [detailedQuestions, setDetailedQuestions] = useState<DetailedQuestion[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState<number[]>([]);
  const [loadedParts, setLoadedParts] = useState<part[]>([]);
  const [loadedPartsWithId, setLoadedPartsWithId] = useState<{ id: number; titre: string }[]>([]);

  const isEditMode = state?.mode === 'edit';

  const loadExistingTest = async (testId: number) => {
    const token = localStorage.getItem('authToken');
    if (!token) { navigate('/login'); return; }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Test non trouvé');

      const data = await response.json();
      const apiTest = data.test;
      const apiParts: { id: number; titre: string; ordre: number }[] = apiTest.parts || [];
      const apiQuestions: any[] = apiTest.questions || [];

      const partIdToIndex = new Map<number, number>();
      apiParts.forEach((p, i) => partIdToIndex.set(p.id, i));

      const parts: part[] = apiParts.map(p => ({ titre: p.titre, numberOfQuestions: 0, numberOfAnswers: 0 }));
      setLoadedParts(parts);
      setLoadedPartsWithId(apiParts);

      const loadedQuestions: DetailedQuestion[] = apiQuestions.map(q => ({
        id: q.id,
        questionNumber: q.question_number,
        numberOfAnswers: q.answer_choices?.length || q.number_of_answers,
        partIndex: q.part_id != null ? (partIdToIndex.get(q.part_id) ?? 0) : 0,
        questionText: q.question_text || '',
        points: q.points || 1,
        isNew: false,
        answerChoices: (q.answer_choices || []).map((c: any) => ({
          letter: c.choice_letter,
          text: c.choice_text || '',
          isCorrect: c.is_correct
        }))
      }));
      setDetailedQuestions(loadedQuestions);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement du test');
      navigate('/my-tests');
    }
  };

  useEffect(() => {
    if (!state) { navigate('/create-test'); return; }

    if (isEditMode && state.testId) {
      loadExistingTest(state.testId);
    } else {
      if (!state.questions || !state.testTitle || !state.parts) {
        navigate('/create-test');
        return;
      }
      const initialQuestions: DetailedQuestion[] = state.questions.map(q => {
        const choices: AnswerChoice[] = [];
        for (let i = 0; i < q.numberOfAnswers; i++) {
          choices.push({ letter: allLetters[i], text: '', isCorrect: false });
        }
        return { ...q, questionText: '', points: 1, answerChoices: choices };
      });
      setDetailedQuestions(initialQuestions);
    }
  }, [state, navigate]);

  const updateQuestion = (qIdx: number, field: 'questionText' | 'points', value: string | number) => {
    setDetailedQuestions(prev => prev.map((q, i) => i !== qIdx ? q : {
      ...q,
      [field]: field === 'points'
        ? (typeof value === 'number' ? (value > 0 ? value : 1) : (parseInt(value as string) || 1))
        : value
    }));
  };

  const updateChoiceText = (qIdx: number, cIdx: number, text: string) => {
    setDetailedQuestions(prev => prev.map((q, i) => i !== qIdx ? q : {
      ...q,
      answerChoices: q.answerChoices.map((c, j) => j !== cIdx ? c : { ...c, text })
    }));
  };

  const toggleCorrect = (qIdx: number, cIdx: number) => {
    setDetailedQuestions(prev => prev.map((q, i) => i !== qIdx ? q : {
      ...q,
      answerChoices: q.answerChoices.map((c, j) => j !== cIdx ? c : { ...c, isCorrect: !c.isCorrect })
    }));
  };

  const addChoice = (qIdx: number) => {
    setDetailedQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx || q.answerChoices.length >= 10) return q;
      const newChoices = [...q.answerChoices, { letter: allLetters[q.answerChoices.length], text: '', isCorrect: false }];
      return { ...q, answerChoices: newChoices, numberOfAnswers: newChoices.length };
    }));
  };

  const removeChoice = (qIdx: number, cIdx: number) => {
    setDetailedQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx || q.answerChoices.length <= 2) return q;
      const newChoices = q.answerChoices
        .filter((_, j) => j !== cIdx)
        .map((c, j) => ({ ...c, letter: allLetters[j] }));
      return { ...q, answerChoices: newChoices, numberOfAnswers: newChoices.length };
    }));
  };

  const removeQuestion = (globalIdx: number) => {
    const toRemove = detailedQuestions[globalIdx];
    if (isEditMode && toRemove?.id && !toRemove?.isNew) {
      setDeletedQuestionIds(prev => [...prev, toRemove.id!]);
    }
    setDetailedQuestions(prev => {
      if (prev.length <= 1) return prev;
      return prev
        .filter((_, i) => i !== globalIdx)
        .map((q, i) => ({ ...q, questionNumber: i + 1 }));
    });
  };

  const addQuestion = (targetPartIndex: number | null) => {
    setDetailedQuestions(prev => {
      let insertAfter = -1;
      prev.forEach((q, i) => {
        if (q.partIndex === targetPartIndex) insertAfter = i;
      });
      const newQuestion: DetailedQuestion = {
        questionNumber: 0,
        numberOfAnswers: 4,
        partIndex: targetPartIndex ?? 0,
        questionText: '',
        points: 1,
        isNew: isEditMode,
        answerChoices: ['A', 'B', 'C', 'D'].map(letter => ({ letter, text: '', isCorrect: false }))
      };
      const updated = [
        ...prev.slice(0, insertAfter + 1),
        newQuestion,
        ...prev.slice(insertAfter + 1)
      ];
      return updated.map((q, i) => ({ ...q, questionNumber: i + 1 }));
    });
  };

  const areAllValid = (): boolean => detailedQuestions.every(isQuestionValid);

  const completedCount = detailedQuestions.filter(isQuestionValid).length;

  const handleCreateSubmit = async (token: string) => {
    try {
      const payload = {
        testTitle: state.testTitle,
        type: 'detailed',
        numberOfQuestions: detailedQuestions.length,
        numberOfAnswers: detailedQuestions[0].numberOfAnswers,
        questions: detailedQuestions.map(q => ({
          questionNumber: q.questionNumber,
          numberOfAnswers: q.numberOfAnswers,
          partIndex: q.partIndex,
          questionText: q.questionText,
          points: q.points,
          answerChoices: q.answerChoices.map(c => ({ letter: c.letter, text: c.text, isCorrect: c.isCorrect }))
        })),
        parts: state.parts || []
      };

      const response = await fetch(`${API_BASE_URL}/api/tests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Erreur lors de la création du test');

      navigate('/my-tests', { state: { message: 'Test détaillé créé avec succès!' } });
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création du test');
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (token: string) => {
    try {
      // 1. Supprimer les questions supprimées
      for (const qId of deletedQuestionIds) {
        const r = await fetch(`${API_BASE_URL}/api/tests/questions/${qId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) throw new Error('Erreur lors de la suppression d\'une question');
      }

      // 2. Mettre à jour les questions existantes, ajouter les nouvelles
      for (const q of detailedQuestions) {
        if (q.isNew) {
          const partId = loadedPartsWithId[q.partIndex]?.id ?? null;
          const r = await fetch(`${API_BASE_URL}/api/tests/${state.testId}/questions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              questionText: q.questionText,
              points: q.points,
              partId,
              answerChoices: q.answerChoices.map(c => ({ letter: c.letter, text: c.text, isCorrect: c.isCorrect }))
            })
          });
          if (!r.ok) throw new Error('Erreur lors de l\'ajout d\'une question');
        } else {
          // Mettre à jour texte et points
          const r = await fetch(`${API_BASE_URL}/api/tests/questions/${q.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ questionText: q.questionText, points: q.points })
          });
          if (!r.ok) throw new Error('Erreur lors de la mise à jour d\'une question');

          // Remplacer les choix de réponses
          const r2 = await fetch(`${API_BASE_URL}/api/tests/questions/${q.id}/choices`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              answerChoices: q.answerChoices.map(c => ({ letter: c.letter, text: c.text, isCorrect: c.isCorrect }))
            })
          });
          if (!r2.ok) throw new Error('Erreur lors de la mise à jour des choix');
        }
      }

      navigate('/my-tests', { state: { message: 'Test mis à jour avec succès!' } });
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde');
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!areAllValid()) {
      alert('Veuillez remplir tous les champs et marquer au moins une bonne réponse par question.');
      return;
    }

    setIsSubmitting(true);
    const token = localStorage.getItem('authToken');
    if (!token) { navigate('/login'); return; }

    if (isEditMode) {
      await handleEditSubmit(token);
    } else {
      await handleCreateSubmit(token);
    }
  };

  if (detailedQuestions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  // Grouper les questions par partie
  const effectiveParts = isEditMode ? loadedParts : (state.parts || []);

  const questionsByPart: { partIndex: number | null; label: string | null; questions: { q: DetailedQuestion; globalIdx: number }[] }[] = [];

  if (effectiveParts && effectiveParts.length > 0) {
    effectiveParts.forEach((part, pIdx) => {
      const qs = detailedQuestions
        .map((q, i) => ({ q, globalIdx: i }))
        .filter(({ q }) => q.partIndex === pIdx);
      if (qs.length > 0) {
        questionsByPart.push({ partIndex: pIdx, label: part.titre, questions: qs });
      }
    });
  } else {
    questionsByPart.push({
      partIndex: null,
      label: null,
      questions: detailedQuestions.map((q, i) => ({ q, globalIdx: i }))
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-800 transition">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                {isEditMode ? 'Édition du test détaillé' : 'Configuration du test détaillé'}
              </p>
              <h1 className="text-xl font-bold text-gray-800">{state.testTitle}</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              <span className={completedCount === detailedQuestions.length ? 'text-green-600 font-semibold' : ''}>
                {completedCount}
              </span>
              /{detailedQuestions.length} complétées
            </span>
            <button
              onClick={handleSubmit}
              disabled={!areAllValid() || isSubmitting}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold transition text-sm ${
                !areAllValid() || isSubmitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <Check className="w-4 h-4" />
              {isSubmitting ? 'Enregistrement...' : isEditMode ? 'Enregistrer' : 'Créer le test'}
            </button>
          </div>
        </div>
        {/* Barre de progression */}
        <div className="h-1 bg-gray-200">
          <div
            className="h-1 bg-green-500 transition-all duration-300"
            style={{ width: `${(completedCount / detailedQuestions.length) * 100}%` }}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-6">
        <div className="space-y-6 bg-white rounded-lg p-6 shadow">
          {questionsByPart.map(({ partIndex, label, questions }) => (
            <div key={partIndex ?? 'nopart'}>
              {/* En-tête de partie */}
              {label && (
                <h2 className="text-base font-bold text-gray-700 mb-2">
                  {label}
                </h2>
              )}

              <div className="space-y-2" key={`questions-${partIndex}`}>
                {questions.map(({ q, globalIdx }) => {
                  const valid = isQuestionValid(q);
                  return (
                    <div
                      key={globalIdx}
                      className="bg-white rounded-lg border border-gray-200 p-4"
                    >
                      {/* Ligne principale : numéro + texte + points + statut + supprimer */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-100 text-blue-700 text-sm font-bold flex items-center justify-center">
                          Q{q.questionNumber}
                        </span>
                        <input
                          type="text"
                          value={q.questionText}
                          onChange={(e) => updateQuestion(globalIdx, 'questionText', e.target.value)}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Texte de la question..."
                        />
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <label className="text-xs text-gray-500 whitespace-nowrap">pts :</label>
                          <input
                            type="number"
                            min="1"
                            value={q.points}
                            onChange={(e) => updateQuestion(globalIdx, 'points', parseInt(e.target.value) || 1)}
                            className="w-14 px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        {valid && (
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeQuestion(globalIdx)}
                          disabled={detailedQuestions.length <= 1}
                          title="Supprimer cette question"
                          className={`flex-shrink-0 p-1 rounded transition ${
                            detailedQuestions.length <= 1
                              ? 'text-gray-200 cursor-not-allowed'
                              : 'text-red-400 hover:text-red-600'
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Choix de réponses */}
                      <div className="ml-12 space-y-1.5">
                        {q.answerChoices.map((c, cIdx) => (
                          <div key={cIdx} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={c.isCorrect}
                              onChange={() => toggleCorrect(globalIdx, cIdx)}
                              className="w-4 h-4 text-blue-600 flex-shrink-0"
                              title="Bonne réponse"
                            />
                            <span className={`text-xs font-bold w-4 flex-shrink-0 ${c.isCorrect ? 'text-green-600' : 'text-gray-400'}`}>
                              {c.letter}
                            </span>
                            <input
                              type="text"
                              value={c.text}
                              onChange={(e) => updateChoiceText(globalIdx, cIdx, e.target.value)}
                              className={`flex-1 px-3 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                c.isCorrect ? 'border-green-300 bg-green-50' : 'border-gray-300'
                              }`}
                              placeholder={`Choix ${c.letter}...`}
                            />
                            <button
                              type="button"
                              onClick={() => removeChoice(globalIdx, cIdx)}
                              disabled={q.answerChoices.length <= 2}
                              className={`flex-shrink-0 p-1 rounded transition ${
                                q.answerChoices.length <= 2
                                  ? 'text-gray-200 cursor-not-allowed'
                                  : 'text-red-400 hover:text-red-600'
                              }`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}

                        {q.answerChoices.length < 10 && (
                          <button
                            type="button"
                            onClick={() => addChoice(globalIdx)}
                            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 font-medium transition mt-1"
                          >
                            <Plus className="w-3 h-3" />
                            Ajouter un choix
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Bouton ajouter une question à cette partie */}
                <button
                  type="button"
                  onClick={() => addQuestion(partIndex)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300 hover:border-blue-400 transition font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter une question{label ? ` à "${label}"` : ''}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bouton bas de page */}
        <div className="flex justify-end mt-6">
          <button
            onClick={handleSubmit}
            disabled={!areAllValid() || isSubmitting}
            className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition ${
              !areAllValid() || isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <Check className="w-5 h-5" />
            {isSubmitting ? 'Enregistrement...' : isEditMode ? 'Enregistrer les modifications' : 'Créer le test'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default DetailedTestConfiguration;
